import React, { useState } from 'react';
import { CheckCircle, Calendar, User, Mail, Phone, Shield, Clock, FileText, DollarSign, Award, AlertCircle, X } from 'lucide-react';

const TaxPreparation: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    preferredTime: '',
    isOver55: false,
    agreeToTerms: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }
    
    if (!formData.preferredTime) {
      newErrors.preferredTime = 'Please select a preferred contact time';
    }
    
    if (!formData.isOver55) {
      newErrors.isOver55 = 'You must be 55 or older to qualify for this offer';
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // In a real application, you would submit the form data to your backend
      console.log('Form submitted:', formData);
      setSubmitted(true);
    }
  };

  // Function to handle going back to the form
  const handleBackToForm = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSubmitted(false);
  };

  const benefits = [
    {
      icon: <Award className="h-10 w-10 text-primary" />,
      title: "Retirement Income Expertise",
      description: "Specialized knowledge in handling retirement accounts, Social Security benefits, and pension distributions."
    },
    {
      icon: <Shield className="h-10 w-10 text-primary" />,
      title: "Senior Tax Deductions",
      description: "Maximize your tax savings with deductions and credits specifically available to those 55 and older."
    },
    {
      icon: <FileText className="h-10 w-10 text-primary" />,
      title: "Healthcare Expense Reporting",
      description: "Proper handling of Medicare, supplemental insurance, and medical expense deductions."
    },
    {
      icon: <DollarSign className="h-10 w-10 text-primary" />,
      title: "Required Minimum Distributions",
      description: "Expert guidance on RMD rules and tax implications for your retirement accounts."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {!submitted ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div>
              <div className="inline-flex items-center bg-primary/10 rounded-full px-4 py-2 text-primary mb-6">
                <DollarSign className="h-5 w-5 mr-2" />
                Limited Time Offer
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Professional Tax Return Preparation
                <span className="block text-primary">Just $99 for Seniors</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8">
                Get your taxes done right by experts who understand the unique tax situations facing adults 55 and older. Our professional preparers will help you maximize deductions and minimize stress.
              </p>
              
              <div className="space-y-4 mb-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <CheckCircle className="h-6 w-6 text-primary" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-gray-900">{benefit.title}</h3>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
                <div className="flex">
                  <AlertCircle className="h-6 w-6 text-yellow-500 flex-shrink-0" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Limited Time Offer</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>This special $99 rate is only available for a limited time and for adults 55 and older. Regular price is $199. Offer valid for basic tax returns only. Additional forms may incur extra charges.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Column - Form */}
            <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Claim Your $99 Tax Preparation Offer
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="fullName" className="block text-lg font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-6 w-6 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className={`pl-12 block w-full rounded-lg border ${errors.fullName ? 'border-red-300' : 'border-gray-300'} py-4 text-gray-900 shadow-sm focus:border-primary focus:ring-primary text-lg`}
                      placeholder="John Smith"
                    />
                  </div>
                  {errors.fullName && (
                    <p className="mt-2 text-red-600">{errors.fullName}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-lg font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-6 w-6 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`pl-12 block w-full rounded-lg border ${errors.email ? 'border-red-300' : 'border-gray-300'} py-4 text-gray-900 shadow-sm focus:border-primary focus:ring-primary text-lg`}
                      placeholder="you@example.com"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-2 text-red-600">{errors.email}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-lg font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-6 w-6 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`pl-12 block w-full rounded-lg border ${errors.phone ? 'border-red-300' : 'border-gray-300'} py-4 text-gray-900 shadow-sm focus:border-primary focus:ring-primary text-lg`}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-2 text-red-600">{errors.phone}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="preferredTime" className="block text-lg font-medium text-gray-700 mb-2">
                    Preferred Contact Time
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Clock className="h-6 w-6 text-gray-400" />
                    </div>
                    <select
                      id="preferredTime"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      className={`pl-12 block w-full rounded-lg border ${errors.preferredTime ? 'border-red-300' : 'border-gray-300'} py-4 text-gray-900 shadow-sm focus:border-primary focus:ring-primary text-lg`}
                    >
                      <option value="">Select a time</option>
                      <option value="morning">Morning (9AM - 12PM)</option>
                      <option value="afternoon">Afternoon (12PM - 5PM)</option>
                      <option value="evening">Evening (5PM - 8PM)</option>
                    </select>
                  </div>
                  {errors.preferredTime && (
                    <p className="mt-2 text-red-600">{errors.preferredTime}</p>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-6">
                      <input
                        id="isOver55"
                        name="isOver55"
                        type="checkbox"
                        checked={formData.isOver55}
                        onChange={handleChange}
                        className={`h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary ${errors.isOver55 ? 'border-red-300' : ''}`}
                      />
                    </div>
                    <div className="ml-3">
                      <label htmlFor="isOver55" className="text-lg font-medium text-gray-700">
                        I am 55 years of age or older
                      </label>
                    </div>
                  </div>
                  {errors.isOver55 && (
                    <p className="mt-1 text-red-600">{errors.isOver55}</p>
                  )}
                  
                  <div className="flex items-start">
                    <div className="flex items-center h-6">
                      <input
                        id="agreeToTerms"
                        name="agreeToTerms"
                        type="checkbox"
                        checked={formData.agreeToTerms}
                        onChange={handleChange}
                        className={`h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary ${errors.agreeToTerms ? 'border-red-300' : ''}`}
                      />
                    </div>
                    <div className="ml-3">
                      <label htmlFor="agreeToTerms" className="text-base text-gray-700">
                        I agree to the terms and conditions
                      </label>
                    </div>
                  </div>
                  {errors.agreeToTerms && (
                    <p className="mt-1 text-red-600">{errors.agreeToTerms}</p>
                  )}
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-4 px-4 rounded-lg text-xl transition-colors duration-200 shadow-md hover:shadow-lg"
                >
                  Claim My $99 Special Offer
                </button>
                
                <p className="text-center text-gray-500 text-sm mt-4">
                  By submitting this form, you agree to be contacted about tax preparation services.
                </p>
              </form>
            </div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-200">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
              <p className="text-xl text-gray-600 mb-6">
                Your request for our $99 tax preparation special has been received. One of our tax professionals will contact you shortly at your preferred time to schedule your appointment.
              </p>
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">What to expect next:</h3>
                <ul className="text-left space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>A tax professional will call you within 24 hours</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>Schedule your in-person or virtual appointment</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>Receive a list of documents to prepare for your appointment</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>Enjoy your professional tax preparation for just $99</span>
                  </li>
                </ul>
              </div>
              <button
                onClick={handleBackToForm}
                className="text-primary hover:text-primary-dark font-medium"
              >
                Return to form
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Trust Indicators Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Why Choose Our Tax Preparation Services?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <Award className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Certified Experts</h3>
              <p className="text-gray-600">All tax preparers are IRS Enrolled Agents or CPAs with senior tax expertise</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <Calendar className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">25+ Years Experience</h3>
              <p className="text-gray-600">Decades of experience helping seniors maximize their tax benefits</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <Shield className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">100% Satisfaction</h3>
              <p className="text-gray-600">Guaranteed accuracy or we'll refund your preparation fee</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <DollarSign className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Senior Discount</h3>
              <p className="text-gray-600">Special $99 rate exclusively for adults 55 and older</p>
            </div>
          </div>
          
          <div className="mt-16 text-center">
            <div className="inline-flex flex-wrap justify-center gap-8">
              <div className="flex items-center">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/3b/IRS_logo.svg/1200px-IRS_logo.svg.png" alt="IRS" className="h-12 w-auto grayscale opacity-70" />
              </div>
              <div className="flex items-center">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c9/AICPA_logo.svg/2560px-AICPA_logo.svg.png" alt="AICPA" className="h-12 w-auto grayscale opacity-70" />
              </div>
              <div className="flex items-center">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/e5/Better_Business_Bureau.svg/1200px-Better_Business_Bureau.svg.png" alt="BBB" className="h-12 w-auto grayscale opacity-70" />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Terms and Conditions */}
      <div className="bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Terms and Conditions</h3>
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                The $99 tax preparation offer is valid for adults 55 years of age or older for basic tax returns only. 
                A basic tax return includes Form 1040 and Schedules A and B.
              </p>
              <p>
                Additional forms and schedules may incur extra charges. Complex returns involving business income, 
                rental properties, or extensive investment transactions are not eligible for this offer.
              </p>
              <p>
                This offer cannot be combined with other discounts or promotions. Valid for the 2024 tax filing season only.
                Offer subject to change without notice.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxPreparation;